<template>
  <!-- the 24px content indent and the top margin exist to clear this shell's
       own left nav rail. horizon/midnight don't render that rail (its
       destinations live in the section nav), so there they are dead space and
       these pages sat further right than every other horizon page. same
       treatment alerts_shell already carries. -->
  <div class="tc-shell" :class="{ 'tc-shell--flush': horizonNavActive }">

    <div class="tc-layout">

      <!-- Collapseable left nav (horizon drives tabs from the sidebar via ?tab=) -->
      <div v-if="!horizonNavActive" class="tc-nav-wrapper" :class="{ 'tc-nav-collapsed': navCollapsed }">
        <div class="tc-nav-inner">
          <button
            v-for="tab in visibleTabs"
            :key="tab.id"
            class="tc-nav-item"
            :class="{ active: activeTab === tab.id }"
            @click="selectTab(tab.id)"
          >
            {{ tab.label }}
          </button>
        </div>
        <button
          class="tc-nav-toggle"
          @click="toggleNav"
          :title="navCollapsed ? 'Expand menu' : 'Collapse menu'"
          :aria-label="navCollapsed ? 'Expand menu' : 'Collapse menu'"
        >
          <i class="fa" :class="navCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
        </button>
      </div>

      <!-- Tab content -->
      <div class="tc-content">

        <!-- tab_history and tab_user_list already render their own page
             banner/subtitle, so skip this generic one there to avoid stacking two -->
        <!-- a tab can take the rail over (tabRail): its own title, an
             eyebrow (the tab's label unless the tab names one), and for a
             part of itself a back the tab handles (railBack), held while
             busy; at its top level a glyph in the back's place. a part can
             also bring actions (icon buttons named by aria-label, a click
             handed back through railAction) whose look carries their state,
             plus that state in words for screen readers; nowrap keeps the
             actions beside the title on a phone
             instead of on a row of their own. no favorite star on a takeover
             or on ERM settings (Chaz 2026-10-01: ERM settings isn't a
             favorite). a takeover also pins the rail under the top bar
             (tc-rail--pin), its height published as --tc-rail-h for what
             sticks below it. the erm tab does all this under horizon /
             midnight. -->
        <FiPageRail
          v-if="showTabBanner"
          ref="pageRail"
          :class="{ 'tc-rail--pin': !!tabRail, 'tc-rail--stuck': !!tabRail && railStuck }"
          :title="tabRail ? tabRail.title : activeTabLabel"
          :eyebrow="tabRail ? (tabRail.eyebrow || activeTabLabel) : ''"
          :back-label="tabRail ? (tabRail.backLabel || '') : ''"
          :back-disabled="!!(tabRail && tabRail.busy)"
          :lead-icon="tabRail && !tabRail.backLabel ? (tabRail.icon || '') : ''"
          :chip="ermTenantChip"
          :no-fav="!!tabRail || activeTab === 'tab_erm'"
          :nowrap-actions="!!(tabRail && tabRail.actions)"
          compact
          @back="tabRailBack"
        >
          <template v-if="tabRail && tabRail.actions" #actions>
            <!-- the part's state in words for a screen reader (Saving...,
                 Saved, an error); the buttons show it to everyone else -->
            <span class="sr-only" aria-live="polite">{{ tabRail.status || '' }}</span>
            <b-button
              v-for="a in tabRail.actions"
              :key="a.id"
              class="tc-rail-act"
              size="sm"
              :variant="a.variant"
              :disabled="a.disabled"
              :title="a.label"
              :aria-label="a.label"
              @click="tabRailAction(a.id)"
            ><i class="fa" :class="a.icon" aria-hidden="true"></i><span class="tc-rail-act-label">&nbsp;{{ a.label }}</span></b-button>
          </template>
          <!-- a tab that ships an explainer gets the standard header control.
               bootstrap-vue registers modals globally by id, so the shell can
               open one that lives inside the tab's own component. -->
          <template v-else-if="activeTabInfoModal" #actions>
            <button
              type="button"
              class="tc-info-btn"
              :title="'About ' + activeTabLabel"
              :aria-label="'About ' + activeTabLabel"
              @click="$bvModal.show(activeTabInfoModal)"
            ><i class="fa fa-info-circle" aria-hidden="true"></i></button>
          </template>
        </FiPageRail>

        <!-- horizon-only landing: a card grid of the settings areas. no ref
             on purpose -- the shell's dirty/load_data plumbing skips it -->
        <div v-if="horizonNavActive" v-show="activeTab === 'tab_overview'" class="tc-overview">
          <p class="tc-ov-sub">Choose a settings area to manage.</p>
          <div class="tc-ov-grid">
            <button
              v-for="card in overviewCards"
              :key="card.id"
              class="tc-ov-card"
              @click="selectTab(card.id)"
            >
              <span class="tc-ov-icon"><i class="fa" :class="card.icon" aria-hidden="true"></i></span>
              <span class="tc-ov-body">
                <span class="tc-ov-title">{{ card.label }}</span>
                <span class="tc-ov-desc">{{ card.desc }}</span>
              </span>
            </button>
          </div>
        </div>

        <tenant-config-general
          v-show="activeTab === 'tab_general'"
          ref="tab_general"
          @showAlert="relayShowAlert"
        />

        <tenant-config-packages
          v-if="showPackages"
          v-show="activeTab === 'tab_packages'"
          ref="tab_packages"
          @showAlert="relayShowAlert"
        />

        <tenant-config-bookings-actuals
          v-if="showBookingsActuals"
          v-show="activeTab === 'tab_bookings_actuals'"
          ref="tab_bookings_actuals"
          @showAlert="relayShowAlert"
        />

        <tenant-config-cust-map-views
          v-if="showMapSettings"
          v-show="activeTab === 'tab_cust_map_views'"
          ref="tab_cust_map_views"
          @showAlert="relayShowAlert"
        />

        <tenant-config-views-reports
          v-if="showViewsReports"
          v-show="activeTab === 'tab_views_reports'"
          ref="tab_views_reports"
          @showAlert="relayShowAlert"
        />

        <tenant-config-alerts
          v-if="showAlerts"
          v-show="activeTab === 'tab_alerts'"
          ref="tab_alerts"
          @showAlert="relayShowAlert"
        />

        <tenant-config-notifications
          v-if="showNotifications"
          v-show="activeTab === 'tab_notifications'"
          ref="tab_notifications"
          @showAlert="relayShowAlert"
        />

        <tenant-config-veh-status-mgmt
          v-if="showVSM"
          v-show="activeTab === 'tab_veh_status_mgmt'"
          ref="tab_veh_status_mgmt"
          @showAlert="relayShowAlert"
        />

        <tenant-config-bus-sos
          v-if="showBusSOS"
          v-show="activeTab === 'tab_bus_sos'"
          ref="tab_bus_sos"
          @showAlert="relayShowAlert"
        />

        <tenant-config-erm
          v-if="showErm"
          v-show="activeTab === 'tab_erm'"
          ref="tab_erm"
          @rail="setTabRail('tab_erm', $event)"
        />

        <tenant-config-data-services
          v-if="showDataServices"
          v-show="activeTab === 'tab_data_services'"
          ref="tab_data_services"
          @showAlert="relayShowAlert"
        />

        <tenant-config-fleet-assistant
          v-if="showFleetAssistant"
          v-show="activeTab === 'tab_fleet_assistant'"
          ref="tab_fleet_assistant"
          @showAlert="relayShowAlert"
        />

        <tenant-config-history
          v-show="activeTab === 'tab_history'"
          ref="tab_history"
          @showAlert="relayShowAlert"
        />

        <user-list
          v-show="activeTab === 'tab_user_list'"
          ref="tab_user_list"
          embedded
        />

      </div>
    </div>

    <b-modal
      id="tc-unsaved-modal"
      title="Unsaved Changes"
      ok-title="Leave without saving"
      cancel-title="Stay on this page"
      ok-variant="danger"
      @ok="confirmTabSwitch"
      @cancel="cancelTabSwitch"
      @hide="onUnsavedModalHide"
    >
      <p>You have unsaved changes on this page. If you continue, your changes will be lost.</p>
    </b-modal>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import horizonNavMixin from '@/nav/horizon_nav_mixin'
import { isFeatureEnabled } from '@/utils/alerts_access'
import { hasVsmAccess } from '@/utils/vsm_access'
import { effectiveUser } from '@/utils/view_as'
import { tenantContextDiffers, tenantDisplayName } from '@/utils/tenant_context'
import TenantConfigGeneral from './tenant_config_general.vue'
import TenantConfigPackages from './tenant_config_packages.vue'
import TenantConfigBookingsActuals from './tenant_config_bookings_actuals.vue'
import TenantConfigCustMapViews from './tenant_config_cust_map_views.vue'
import TenantConfigViewsReports from './tenant_config_views_reports.vue'
import TenantConfigAlerts from './tenant_config_alerts.vue'
import TenantConfigNotifications from './tenant_config_notifications.vue'
import TenantConfigVehStatusMgmt from './tenant_config_veh_status_mgmt.vue'
import TenantConfigBusSos from './tenant_config_bus_sos.vue'
import TenantConfigErm from './tenant_config_erm.vue'
import TenantConfigDataServices from './tenant_config_data_services.vue'
import TenantConfigFleetAssistant from './tenant_config_fleet_assistant.vue'
import TenantConfigHistory from './tenant_config_history.vue'
import UserList from './user_list.vue'
import { registerDirtyCheck, unregisterDirtyCheck } from '@/utils/build_watcher'

const TAB_LABELS = {
  tab_overview:         'Overview',
  tab_general:          'General',
  tab_packages:         'Packages & Billing',
  tab_bookings_actuals: 'Bookings & Actuals',
  tab_cust_map_views:   'Map Settings',
  tab_views_reports:    'Views & Reports',
  tab_alerts:           'Alerts',
  tab_notifications:    'Notifications',
  tab_veh_status_mgmt:  'Vehicle Status Manager',
  tab_bus_sos:          'Bus SOS Settings',
  tab_erm:              'Event Response Manager',
  tab_data_services:    'Data Services',
  tab_fleet_assistant:  'Fleet Assistant',
  tab_history:          'Settings History',
  tab_user_list:        'Manage Users',
}

// page rail titles. the rail is the one surface outside the Settings
// heading, so it says Settings; the classic nav and the overview cards sit
// under that heading and stay bare (TAB_LABELS). tab_history and
// tab_user_list render their own rails and are left out.
const RAIL_TITLES = {
  tab_overview:         'Settings',
  tab_general:          'General Settings',
  tab_packages:         'Packages & Billing',
  tab_bookings_actuals: 'Bookings & Actuals Settings',
  tab_cust_map_views:   'Map Settings',
  tab_views_reports:    'Views & Reports Settings',
  tab_alerts:           'Alerts Settings',
  tab_notifications:    'Notifications Settings',
  tab_veh_status_mgmt:  'Vehicle Status Settings',
  tab_bus_sos:          'Bus SOS Settings',
  tab_erm:              'Event Response Manager Settings',
  tab_data_services:    'Data Services Settings',
  tab_fleet_assistant:  'Fleet Assistant Settings',
}

// tabs that ship an explainer modal, by the modal id their own component
// registers. The shell only opens it; the content belongs to the tab.
const TAB_INFO_MODALS = {
  tab_packages: 'tc-packages-pricing-info',
}

// exported for the rail-title spec only
export { TAB_LABELS, RAIL_TITLES }

// card copy for the horizon overview landing (no per-tab descriptions
// existed anywhere to reuse)
const TAB_OVERVIEW_META = {
  tab_general:          { icon: 'fa-sliders',              desc: 'Company profile, branding, and core account details.' },
  tab_packages:         { icon: 'fa-cubes',                desc: 'See which product packages this account has, and what they cost.' },
  tab_bookings_actuals: { icon: 'fa-calendar-check-o',     desc: 'How bookings import and match against recorded actuals.' },
  tab_cust_map_views:   { icon: 'fa-map-o',                desc: 'Default map views, labels, and visibility options.' },
  tab_views_reports:    { icon: 'fa-bar-chart',            desc: 'Which report pages are available and how they behave.' },
  tab_alerts:           { icon: 'fa-bell-o',               desc: 'Alert rules and thresholds for fleet events.' },
  tab_notifications:    { icon: 'fa-envelope-o',           desc: 'Notification templates and recipient settings.' },
  tab_veh_status_mgmt:  { icon: 'fa-wrench',               desc: 'Status board, inspection, and maintenance options.' },
  tab_bus_sos:          { icon: 'fa-exclamation-triangle', desc: 'Emergency SOS recipients, range, and vehicle coverage.' },
  tab_erm:              { icon: 'fa-flag',                 desc: 'Categories, recipients, the public form, and the huddle.' },
  tab_data_services:    { icon: 'fa-exchange',             desc: 'Data sync services for drivers and vehicles.' },
  tab_fleet_assistant:  { icon: 'fa-magic',                desc: 'Fleet Assistant access and behavior for this account.' },
  tab_history:          { icon: 'fa-history',              desc: 'A log of settings changes on this account.' },
  tab_user_list:        { icon: 'fa-users',                desc: 'Add users, set roles, and manage access.' },
}

export default {
  name: 'tenant_config_shell',
  mixins: [horizonNavMixin],
  components: {
    TenantConfigGeneral,
    TenantConfigPackages,
    TenantConfigBookingsActuals,
    TenantConfigCustMapViews,
    TenantConfigViewsReports,
    TenantConfigAlerts,
    TenantConfigNotifications,
    TenantConfigVehStatusMgmt,
    TenantConfigBusSos,
    TenantConfigErm,
    TenantConfigDataServices,
    TenantConfigFleetAssistant,
    TenantConfigHistory,
    UserList,
  },
  data() {
    return {
      activeTabIndex: 0,
      // per-tab rail takeovers ({ title, backLabel } or null), see tabRail
      tabRails: {},
      pendingTabIndex: null,
      pendingNavNext: null,
      modalOkClicked: false,
      navCollapsed: false,
      navUserToggled: false,
      // the pinned rail has content scrolling under it: it lifts with a
      // shadow (tc-rail--stuck), flat again at the top
      railStuck: false,
    }
  },
  // the erm tab can show a tenant other than the navbar's (?tenant=, from the
  // Event Log's and the huddle's links), and a link that drops or changes it
  // on the same tab (its own menu row does) reloads that tenant's settings
  // over any edits: ask first, through the same dialog as leaving. a ?tab=
  // change is left to the tab watcher, which asks on its own.
  beforeRouteUpdate(to, from, next) {
    const sameTab = String(to.query.tab || '') === String(from.query.tab || '')
    const tenantChanged = String(to.query.tenant || '') !== String(from.query.tenant || '')
    if (sameTab && tenantChanged && this.anyTabDirty) {
      this.pendingNavNext = next
      this.pendingTabIndex = null
      this.modalOkClicked = false
      this.$bvModal.show('tc-unsaved-modal')
      return
    }
    next()
  },
  beforeRouteLeave(to, from, next) {
    if (this.anyTabDirty) {
      this.pendingNavNext = next
      this.pendingTabIndex = null
      this.modalOkClicked = false
      this.$bvModal.show('tc-unsaved-modal')
    } else {
      next()
    }
  },
  mounted() {
    this._beforeUnloadHandler = (e) => {
      if (this.anyTabDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', this._beforeUnloadHandler)
    // FI-2181: the build watcher never reloads over unsaved settings
    this._buildDirtyCheck = () => this.anyTabDirty
    registerDirtyCheck(this._buildDirtyCheck)

    this._onResize = () => {
      // a height change can cross the pin's min-height query without
      // resizing the rail itself
      this.syncRailHeight()
      // Keep the nav responsive to window width unless the user has explicitly toggled it
      if (this.navUserToggled) return
      this.navCollapsed = window.innerWidth < 1600
    }
    window.addEventListener('resize', this._onResize)
    this._onResize()

    // classic restores the last tab; under horizon a bare /settings lands on
    // the overview (index 0) and the ?tab= query is the source of truth
    const saved = localStorage.getItem('tc_active_tab')
    if (saved && !this.horizonNavActive) {
      const idx = this.visibleTabRefKeys.indexOf(saved)
      if (idx !== -1) this.activeTabIndex = idx
    }
    // horizon sidebar deep-links tabs via ?tab= — the query beats the saved
    // tab, and the url is kept honest so the sidebar highlight always matches.
    // classic behavior is untouched (no query written unless horizon).
    const qTab = this.$route.query && this.$route.query.tab
    if (qTab) {
      const qIdx = this.visibleTabRefKeys.indexOf(qTab)
      if (qIdx !== -1) this.activeTabIndex = qIdx
    }
    if (this.horizonNavActive) this._syncTabQuery()
    this.$nextTick(() => this.observeRail())
    // one stuck check per frame while the page scrolls
    this._onScroll = () => {
      if (this._scrollFrame) return
      this._scrollFrame = window.requestAnimationFrame(() => {
        this._scrollFrame = 0
        this.syncRailStuck()
      })
    }
    window.addEventListener('scroll', this._onScroll, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this._beforeUnloadHandler)
    unregisterDirtyCheck(this._buildDirtyCheck)
    window.removeEventListener('resize', this._onResize)
    window.removeEventListener('scroll', this._onScroll)
    if (this._scrollFrame) window.cancelAnimationFrame(this._scrollFrame)
    if (this._railObserver) this._railObserver.disconnect()
    document.documentElement.style.removeProperty('scroll-padding-top')
  },
  // the pinned rail's element changes with the tab, the theme (rail vs
  // legacy banner) and showTabBanner, so the observer follows it after
  // every render
  updated() {
    this.observeRail()
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    isEffectiveAdmin() {
      return this.user.role === 'admin' && this.viewAs !== 'non-admin'
    },
    tenantFeatures() {
      return (this.user.features && this.user.features[this.user.tenant]) || {}
    },
    // Staff see every settings tab, whether or not the account has bought the
    // package. They are the people who turn a package on, and they support
    // accounts that have not: a tab that vanishes with the flag means configuring
    // a package is a two-step dance through the Packages tab and back.
    //
    // This reverses the 2026-09-10 decision to hide a disabled package's tab from
    // admins too. The customer-facing half of that still stands -- the nav
    // registry's PACKAGE_GATES keep whole rail sections hidden for customers, and
    // a customer-admin still sees only what the account has bought, because
    // isEffectiveAdmin reads the REAL role and stays false in muggle preview.
    packageTabsAlwaysVisible() {
      return this.isEffectiveAdmin
    },
    showPackages() {
      // everyone who can reach Settings at all -- staff and the tenant's own
      // customer-admins. Staff get the toggles; customer-admins get the same
      // table read-only (tenant_config_packages gates the switches and the Save
      // button on its own canEdit), which is what makes it the one place to see
      // what this account has and what it costs.
      return true
    },
    showBookingsActuals() {
      return this.packageTabsAlwaysVisible || isFeatureEnabled(this.tenantFeatures.actuals_enabled)
    },
    showViewsReports() {
      return this.packageTabsAlwaysVisible || isFeatureEnabled(this.tenantFeatures.views_reports_enabled)
    },
    showAlerts() {
      return this.packageTabsAlwaysVisible || isFeatureEnabled(this.tenantFeatures.alerts_enabled)
    },
    showNotifications() {
      return this.packageTabsAlwaysVisible || isFeatureEnabled(this.tenantFeatures.alerts_enabled)
    },
    showVSM() {
      return this.packageTabsAlwaysVisible || hasVsmAccess(this.tenantFeatures)
    },
    showBusSOS() {
      return this.packageTabsAlwaysVisible || isFeatureEnabled(this.tenantFeatures.bus_sos_enabled)
    },
    // FI-2172: the erm settings body (ops_desk_settings.vue) as a tab. both halves of the
    // registry's answer restated here: the package flag (the erm_settings item gate) and the
    // settings section gate, admin / customer-admin -- read off the effective user so a muggle
    // preview (an admin viewing as the tenant's customer-admin) agrees with the sidebar
    showErm() {
      if (this.packageTabsAlwaysVisible)
        return true
      return isFeatureEnabled(this.ermTenantFeatures.ops_desk_enabled)
        && effectiveUser(this.user, this.viewAs).role === 'customer-admin'
    },
    // the erm tab follows the tenant its body shows (?tenant= first, which
    // ops_desk_settings reads), not only the navbar's: the Event Log's and
    // the huddle's links carry the tenant they came from, and a fleet admin
    // with sibling accounts can be on one the navbar isn't
    ermTenantFeatures() {
      const t = this.$route && this.$route.query && this.$route.query.tenant
      const all = (this.user && this.user.features) || {}
      return (t && all[t]) || this.tenantFeatures
    },
    // the tenant chip the ops pages carry (utils/tenant_context): the erm
    // tab names the tenant it shows whenever that isn't the navbar's
    ermTenantChip() {
      if (this.activeTab !== 'tab_erm' || !tenantContextDiffers(this.$route, this.user)) return ''
      return tenantDisplayName(this.user, this.$route.query.tenant)
    },
    showDataServices() {
      return this.packageTabsAlwaysVisible || isFeatureEnabled(this.tenantFeatures.data_services_enabled)
    },
    showFleetAssistant() {
      return this.isEffectiveAdmin || (this.user.tenant === 'anderson-759g468f' && this.user.role === 'customer-admin')
    },
    // yard-only accounts have no Fleet Map to configure (only the SOS Yard Map), so no Map Settings
    showMapSettings() {
      return this.packageTabsAlwaysVisible || !isFeatureEnabled(this.tenantFeatures.bus_sos_yard_only)
    },
    visibleTabRefKeys() {
      const keys = []
      // the overview landing exists only under horizon; classic keeps its
      // own rail and lands on General exactly as before
      if (this.horizonNavActive) keys.push('tab_overview')
      keys.push('tab_general')
      if (this.showBookingsActuals) keys.push('tab_bookings_actuals')
      if (this.showMapSettings) keys.push('tab_cust_map_views')
      if (this.showViewsReports) keys.push('tab_views_reports')
      if (this.showAlerts) keys.push('tab_alerts')
      if (this.showNotifications) keys.push('tab_notifications')
      if (this.showVSM) keys.push('tab_veh_status_mgmt')
      if (this.showBusSOS) keys.push('tab_bus_sos')
      if (this.showErm) keys.push('tab_erm')
      if (this.showDataServices) keys.push('tab_data_services')
      if (this.showFleetAssistant) keys.push('tab_fleet_assistant')
      keys.push('tab_history')
      keys.push('tab_user_list')
      // last, under Manage Users: it is the account's bill rather than another
      // thing to configure
      if (this.showPackages) keys.push('tab_packages')
      return keys
    },
    visibleTabs() {
      return this.visibleTabRefKeys.map(id => ({ id, label: TAB_LABELS[id] }))
    },
    // tab_erm is left off where ERM is bought: its settings live under the
    // ERM menu (2026-10-01), so the landing doesn't offer a second way in.
    // where it isn't, the ERM menu is absent and only staff see the tab
    // (packageTabsAlwaysVisible), so the card stays as their way in, like the
    // registry's staff-only Settings row. the tab itself always stays (the ERM
    // menu row and classic's left nav open it)
    overviewCards() {
      const ermInMenu = isFeatureEnabled(this.tenantFeatures.ops_desk_enabled)
      return this.visibleTabRefKeys
        .filter(id => id !== 'tab_overview' && !(id === 'tab_erm' && ermInMenu))
        .map(id => ({
          id,
          label: TAB_LABELS[id],
          icon: (TAB_OVERVIEW_META[id] || {}).icon || 'fa-cog',
          desc: (TAB_OVERVIEW_META[id] || {}).desc || '',
        }))
    },
    activeTab() {
      return this.visibleTabRefKeys[this.activeTabIndex] || null
    },
    activeTabInfoModal() {
      return TAB_INFO_MODALS[this.activeTab] || null
    },
    activeTabLabel() {
      return RAIL_TITLES[this.activeTab] || TAB_LABELS[this.activeTab] || ''
    },
    // tab_history and tab_user_list carry their own banner/subtitle already,
    // so the shell's generic one would just duplicate theirs
    // the active tab's rail takeover, when it has one up
    tabRail() {
      return this.tabRails[this.activeTab] || null
    },
    showTabBanner() {
      return !!this.activeTabLabel && this.activeTab !== 'tab_history' && this.activeTab !== 'tab_user_list'
    },
    anyTabDirty() {
      return this.visibleTabRefKeys.some(key => {
        const ref = this.$refs[key]
        return ref && ref.isDirty
      })
    },
  },
  watch: {
    // horizon sidebar navigation lands here as a query change; route it
    // through selectTab so the unsaved-changes guard still runs
    '$route.query.tab'(val) {
      if (!val || val === this.activeTab) return
      if (this.visibleTabRefKeys.indexOf(val) === -1) return
      this.selectTab(val)
    },
    // forward-sync: a completed tab switch (direct or via the confirm modal)
    // updates the url so the sidebar highlight follows. horizon-only so
    // classic urls stay byte-identical.
    activeTab() {
      if (this.horizonNavActive) this._syncTabQuery()
    },
    // a tab's drill-in move (the erm phone drill-in's rail.motion, one seq
    // per move) slides the header's words the way the body slides
    tabRail(val) {
      const m = val && val.motion
      if (!m || m.seq === this._railMotionSeq) return
      this._railMotionSeq = m.seq
      this.$nextTick(() => this.slideRailText(m.dir))
    },
  },
  methods: {
    setTabRail(key, rail) {
      this.$set(this.tabRails, key, rail || null)
    },
    tabRailBack() {
      const ref = this.$refs[this.activeTab]
      if (ref && typeof ref.railBack === 'function') ref.railBack()
    },
    // a pinned rail's height, for what sticks below it (the erm categories
    // table's column header) and for scroll margins: --tc-rail-h on the
    // shell, 0 when nothing is pinned. a ResizeObserver follows the rail as
    // its title wraps or the pane resizes (the fi_favorites_bar pattern)
    observeRail() {
      const el = this.tabRail && this.$refs.pageRail ? this.$refs.pageRail.$el : null
      if (el === this._observedRail) {
        this.syncRailHeight()
        return
      }
      if (this._railObserver) this._railObserver.disconnect()
      this._observedRail = el
      if (el && typeof ResizeObserver !== 'undefined') {
        this._railObserver = new ResizeObserver(() => this.syncRailHeight())
        this._railObserver.observe(el)
      }
      this.syncRailHeight()
    },
    // a short window leaves the rail in the flow (the pin rule's min-height
    // query), so it counts only while it is actually sticky. the document's
    // scroll-padding-top follows, so keyboard focus and scrollIntoView stop
    // below the fixed top bar and the pinned rail
    syncRailHeight() {
      const el = this._observedRail
      const pinned = !!(el && el.getBoundingClientRect && window.getComputedStyle(el).position === 'sticky')
      const h = pinned ? Math.ceil(el.getBoundingClientRect().height) : 0
      if (this.$el && this.$el.style) this.$el.style.setProperty('--tc-rail-h', h + 'px')
      const root = document.documentElement
      if (this.horizonNavActive && this.tabRail) root.style.setProperty('scroll-padding-top', 'calc(var(--fi-hz-top-offset, 52px) + ' + h + 'px)')
      else root.style.removeProperty('scroll-padding-top')
      this.syncRailBleed()
      this.syncRailStuck()
    },
    // the content pane's gutters beside the rail (the rail's own box, its
    // parent, does not move when the stuck rail bleeds), for the stuck
    // rail to span the pane edge to edge
    syncRailBleed() {
      const el = this._observedRail
      const box = el && el.parentElement
      const pane = el && el.closest ? el.closest('.fi-hz-content') : null
      let l = 0
      let r = 0
      if (box && pane) {
        const b = box.getBoundingClientRect()
        const p = pane.getBoundingClientRect()
        l = Math.max(0, Math.round(b.left - p.left))
        r = Math.max(0, Math.round(p.right - b.right))
      }
      if (this.$el && this.$el.style) {
        this.$el.style.setProperty('--tc-bleed-l', l + 'px')
        this.$el.style.setProperty('--tc-bleed-r', r + 'px')
      }
    },
    // stuck: pinned and at its sticky top with the page scrolled, so
    // content is passing under it
    syncRailStuck() {
      const el = this._observedRail
      let stuck = false
      if (el && this.tabRail && window.scrollY > 0 && el.getBoundingClientRect) {
        const cs = window.getComputedStyle(el)
        stuck = cs.position === 'sticky' && el.getBoundingClientRect().top <= (parseFloat(cs.top) || 0) + 0.5
      }
      if (stuck !== this.railStuck) this.railStuck = stuck
    },
    // the words (eyebrow + title) arrive from the side the body's screen
    // does, a shorter way; the back or glyph and the actions fade in. the
    // web animations api leaves nothing behind once it ends
    slideRailText(dir) {
      const rail = this.$refs.pageRail && this.$refs.pageRail.$el
      if (!rail || !rail.querySelector) return
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      const text = rail.querySelector('.fi-pr-text')
      if (!text || typeof text.animate !== 'function') return
      const x = dir === 'back' ? -12 : 12
      const ease = 'cubic-bezier(0.2, 0.8, 0.2, 1)'
      text.animate([{ opacity: 0, transform: 'translateX(' + x + 'px)' }, { opacity: 1, transform: 'none' }], { duration: 240, easing: ease })
      rail.querySelectorAll('.fi-pr-back, .fi-pr-lead, .fi-pr-actions').forEach(el => {
        el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: ease })
      })
    },
    tabRailAction(id) {
      const ref = this.$refs[this.activeTab]
      if (ref && typeof ref.railAction === 'function') ref.railAction(id)
    },
    toggleNav() {
      this.navCollapsed = !this.navCollapsed
      this.navUserToggled = true
    },
    relayShowAlert(msg, variant, autoclose) {
      this.$emit('showAlert', msg, variant, autoclose)
    },
    _syncTabQuery() {
      if (this.$route.name !== 'settings' || !this.activeTab) return
      if (this.$route.query.tab === this.activeTab) return
      this.$router.replace({ query: { ...this.$route.query, tab: this.activeTab } }).catch(() => {})
    },
    selectTab(id) {
      if (this._confirmingTabSwitch) return
      const newIndex = this.visibleTabRefKeys.indexOf(id)
      if (newIndex === this.activeTabIndex) return
      const prevRef = this.$refs[this.activeTab]
      if (prevRef && prevRef.isDirty) {
        this.pendingTabIndex = newIndex
        this.modalOkClicked = false
        this.$bvModal.show('tc-unsaved-modal')
      } else {
        this.activeTabIndex = newIndex
        // the overview is a horizon landing, not a real tab; keep classic's
        // saved-tab restore pointing at the last real tab
        if (id !== 'tab_overview') localStorage.setItem('tc_active_tab', id)
        this.$nextTick(() => {
          const ref = this.$refs[id]
          if (ref && ref.load_data) ref.load_data(true)
        })
      }
    },
    confirmTabSwitch() {
      this.modalOkClicked = true
      if (this.pendingNavNext) {
        const next = this.pendingNavNext
        this.pendingNavNext = null
        next()
      } else {
        const prevRef = this.$refs[this.activeTab]
        if (prevRef && prevRef.isDirty) {
          prevRef.load_data()
        }
        this._confirmingTabSwitch = true
        const destId = this.visibleTabRefKeys[this.pendingTabIndex]
        this.activeTabIndex = this.pendingTabIndex
        if (destId !== 'tab_overview') localStorage.setItem('tc_active_tab', destId)
        this.pendingTabIndex = null
        this.$nextTick(() => {
          this._confirmingTabSwitch = false
          const ref = this.$refs[destId]
          if (ref && ref.load_data) ref.load_data(true)
        })
      }
    },
    cancelTabSwitch() {
      if (this.pendingNavNext) {
        const next = this.pendingNavNext
        this.pendingNavNext = null
        next(false)
      }
      this.pendingTabIndex = null
      // the sidebar click already rewrote ?tab= before the guard fired;
      // put the url back on the tab the user chose to stay on
      if (this.horizonNavActive) this._syncTabQuery()
      const correctIndex = this.activeTabIndex
      setTimeout(() => {
        const focusedEl = document.activeElement
        if (focusedEl && this.$el.contains(focusedEl)) {
          focusedEl.blur()
        }
        const refKey = this.visibleTabRefKeys[correctIndex]
        const ref = refKey ? this.$refs[refKey] : null
        if (ref && ref.scrollToFirstDirtyField) {
          ref.scrollToFirstDirtyField()
        }
      }, 300)
    },
    onUnsavedModalHide() {
      if (!this.modalOkClicked) {
        this.pendingTabIndex = null
      }
      this.modalOkClicked = false
    },
  },
}
</script>

<style scoped>
/* a tabRail takeover pins the page rail under the top bar (horizon /
   midnight, where .fi-hz-root pads by the bar's height), above the page's
   own sticky and positioned layers (the erm table header at 2, the tag
   input's suggestions at 50) and below the chrome (1020 and up). its
   background is opaque in every theme. a short window (landscape phone,
   a zoomed browser) keeps it in the flow, where the bars would otherwise
   take most of the viewport */
@media (min-height: 500px) {
  .fi-hz-root .tc-rail--pin {
    position: sticky;
    top: var(--fi-hz-top-offset, 52px);
    z-index: 60;
  }
}
/* a tabRail takeover's actions (see the FiPageRail above): labelled buttons
   on a desktop, icon-only thumb-sized boxes on a phone like the erm rails'
   (erm-mobile.css) */
.tc-rail-act {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
@media (max-width: 600px) {
  .tc-rail-act {
    min-width: 40px;
    min-height: 40px;
    padding-left: 10px;
    padding-right: 10px;
  }
  .tc-rail-act .fa { font-size: 17px; line-height: 1; }
  .tc-rail-act-label { display: none; }
}
/* the state change is the fill, so it eases rather than snaps */
.tc-rail-act { transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease; }
/* a save that lands: the check gives one small bounce as Save turns green */
@keyframes tc-saved-pop {
  0% { transform: scale(1); }
  45% { transform: scale(1.3); }
  100% { transform: scale(1); }
}
.tc-rail-act.btn-outline-success .fa { animation: tc-saved-pop 0.36s cubic-bezier(0.3, 1.5, 0.5, 1); }
/* content scrolling under the pinned rail lifts it with a soft shadow
   and spreads it across the content pane: the band bleeds through the
   pane's gutters (--tc-bleed-l / -r, measured by syncRailBleed) while its
   padding grows by the same amount, so the back, the title and the buttons
   hold still and only the band widens. 16px is FiPageRail's own side
   padding. the shadow is a gradient strip hung under the band, not a
   box-shadow, so it runs the band's full width with no tapered ends, as if
   the band went on past both edges (midnight.css deepens it). the sticky
   rail is the strip's containing block; its padding box starts inside the
   3px accent edge, so the strip reaches back under it. pinned, the band is
   app chrome rather than a page header, so its 3px accent edge keeps going
   left, past the pane's edge (off the screen on a phone, under the sidebar
   on a desktop), and fades on the way; the padding takes up the 3px, so
   the content still holds still */
.fi-hz-root .tc-rail--pin {
  transition: margin 0.2s ease, padding 0.2s ease, border-radius 0.2s ease, border-left-color 0.2s ease;
}
.fi-hz-root .tc-rail--pin::after {
  content: '';
  position: absolute;
  left: -3px;
  right: 0;
  top: 100%;
  height: 12px;
  background: linear-gradient(to bottom, rgba(16, 42, 67, 0.18), rgba(16, 42, 67, 0.06) 45%, rgba(16, 42, 67, 0));
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
}
.fi-hz-root .tc-rail--stuck::after { opacity: 1; }
.fi-hz-root .tc-rail--stuck {
  margin-left: calc(-1 * var(--tc-bleed-l, 0px) - 3px);
  margin-right: calc(-1 * var(--tc-bleed-r, 0px));
  padding-left: calc(19px + var(--tc-bleed-l, 0px));
  padding-right: calc(16px + var(--tc-bleed-r, 0px));
  border-radius: 0;
  border-left-color: transparent;
}
@media (prefers-reduced-motion: reduce) {
  .tc-rail-act.btn-outline-success .fa { animation: none; }
  .fi-hz-root .tc-rail--pin,
  .fi-hz-root .tc-rail--pin::after { transition: none; }
}
/* bootstrap's link blue is about 3.4:1 on the rail band; the ops ink clears
   AA. disabled keeps bootstrap's grey (midnight.css repaints both) */
.tc-rail-act.btn-link:not(:disabled) { color: var(--fi-ops-accent-strong, #305b80); }
.tc-rail-act.btn-link:not(:disabled):hover { color: var(--fi-ops-accent-deep, #1f3f5a); }

/* the standard header explainer control, rail-aligned */
.tc-info-btn {
  background: none;
  border: 0;
  padding: 0 2px;
  font-size: 17px;
  line-height: 1;
  color: var(--fi-text-muted);
  cursor: pointer;
}
.tc-info-btn:hover {
  color: var(--fi-text);
}

/* ── Layout ─────────────────────────────────────────────────────────── */
.tc-layout {
  display: flex;
  align-items: flex-start;
}

/* ── Left nav wrapper (handles collapse transition) ─────────────────── */
.tc-nav-wrapper {
  position: relative;
  display: flex;
  flex-shrink: 0;
}

.tc-nav-inner {
  width: 250px;
  min-width: 250px;
  overflow: hidden;
  transition: width 0.22s ease, min-width 0.22s ease;
  background-color: #e8eef5;
  border-right: 2px solid #dee2e6;
  padding: 12px 0;
}

.tc-nav-collapsed .tc-nav-inner {
  width: 0;
  min-width: 0;
  border-right: none;
  padding: 0;
}

/* ── Nav items ──────────────────────────────────────────────────────── */
.tc-nav-item {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  color: #333;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  outline: none;
}

.tc-nav-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 0;
  height: 1px;
  background-color: #c8d8e8;
}

.tc-nav-item:last-child {
  border-bottom: none;
}

.tc-nav-item:hover {
  background-color: #d0dfee;
}

.tc-nav-item:hover::before {
  content: '';
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  border: 1px solid #a0b8d0;
  pointer-events: none;
}

/* ── Horizon overview landing (card grid of settings areas) ─────────── */
.tc-overview {
  text-align: left;
  padding: 4px 2px;
}

.tc-ov-sub {
  color: #555;
  font-size: 14px;
  margin-bottom: 18px;
}

.tc-ov-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.tc-ov-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #d0d8e4;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(70, 130, 180, 0.08);
  cursor: pointer;
  text-align: left;
  transition: box-shadow 0.15s, border-color 0.15s;
}

.tc-ov-card:hover {
  border-color: steelblue;
  box-shadow: 0 2px 8px rgba(70, 130, 180, 0.18);
}

.tc-ov-card:focus {
  outline: none;
}

.tc-ov-card:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(70, 130, 180, 0.35);
}

.tc-ov-icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(70, 130, 180, 0.12);
  color: steelblue;
  font-size: 15px;
}

.tc-ov-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.tc-ov-title {
  font-size: 14px;
  font-weight: 700;
  color: #222;
}

.tc-ov-desc {
  font-size: 12.5px;
  color: #666;
  line-height: 1.4;
}

.tc-nav-item:focus {
  outline: none;
  box-shadow: none;
}

.tc-nav-item.active {
  background-color: var(--fi-rail-accent) !important;
  color: white !important;
  font-weight: bold;
  border-right: 3px solid #2e6da4;
}

.tc-nav-item.active::before {
  display: none;
}

/* ── Collapse toggle ────────────────────────────────────────────────── */
.tc-nav-toggle {
  position: absolute;
  top: 12px;
  right: -20px;
  width: 20px;
  height: 36px;
  background: #e8eef5;
  border: 1px solid #c8d8e8;
  border-left: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a6785;
  font-size: 11px;
  padding: 0;
  z-index: 10;
  transition: background 0.15s;
}

.tc-nav-toggle:hover {
  background: #d0dfee;
  color: var(--fi-rail-accent);
}

.tc-nav-toggle:focus {
  outline: none;
}

/* ── Content area ───────────────────────────────────────────────────── */
.tc-shell {
  margin-top: 20px;
}

.tc-content {
  flex: 1;
  min-width: 0;
  padding-left: 24px;
}

/* no in-page nav rail to clear (see the template note): the pages inside
   supply their own gutters, so hand the space back to them */
.tc-shell--flush {
  margin-top: 0;
}

.tc-shell--flush .tc-content {
  padding-left: 0;
}

/* ── Per-tab page banner (thin, mirrors the active left-nav label) ──── */
</style>
